/**
 * Utility functions for working with forms. This object can automatically
 * initialize the fields in the DOM to the correct values based on a JSON object
 * and can also read from the DOM in order to create a JSON representation of
 * the fields. For example, you could map between
 *
 * <input name="name" value="Batman">
 *
 * and
 *
 * { name: 'Batman' }
 *
 * Only works with fields that have a single explicit name. If you have complex
 * fields (e.g. array fields that can have multiple values), just don't give the
 * form element a name and it will be ignored.  You can just read the value
 * yourself.
 */

import $ from 'jquery'

import _ from 'lodash'
import DateInput, {getIsoDate} from 'scoreboard/components/date-input'
import selects from 'scoreboard/components/selects'
import i18n from 'scoreboard/components/i18n'

// note for writeToDom you want to be sure you are returning nodes that aren't
// visible because they typically won't visible yet when rendering the form
var query = 'input[name], select[name], textarea[name], button[name], ' +
	'.labeled-toggle[name], .calendar-period-selector[name], ' +
	'.period-selector[name], .date-multi-selector[name]'

var debug = false

var formHelper = {

	/**
	 * Initialize the DOM with the values found in the given model.
	 *
	 * @param data
	 *            the data to write
	 * @param el
	 *            the DOM element containing the form elements to be initialized
	 * @param clearEmpty
	 *            when true, clears out all fields not explicitly mentioned in
	 *            the passed-in data
	 */
	writeToDom: function(data, el, clearEmpty) {
		if (!data || !el) {
			return
		}
		// unwrap Backbone models
		let unwrappedData = data.attributes || data
		$(el).find(query).each(function(index, control) {
			// important to use attr to pick up names for our custom controls
			var name = $(control).attr('name')
			var value = unwrappedData[name]
			if (clearEmpty || !_.isUndefined(value)) {
				if (control.type === 'checkbox') {
					// issue 8400 don't set values for undefined properties
					// Only check the box if the value is true
					$(control).attr('checked', value === 'true' || value === true)
				} else if (control.type === 'radio') {
					if (value == $(control).attr('value')) {
						$(control).prop('checked', true)
					}
				} else if (!_.isUndefined($(control).data('rome-id'))) {
					DateInput.setValue(control, value)
				} else if ($(control).data('val')) {
					$(control).data('val')(value)
				} else if ($(control).data('view') &&
						control.tagName.toLowerCase() !== 'select') {
					$(control).data('view').val(value)
				} else {
					// issue 4995 don't set values for undefined properties
					if (value === null) {
						value = ''
					}
					// for booleans you need to convert to the text 'true' or 'false',
					if (_.isBoolean(value)) {
						value = '' + value
					}
					$(control).val(value)
					// have to let the select know a new value was set
					if (control.tagName.toLowerCase() === 'select') {
						// If we have multiple selected values, we're populating a
						// filtering select
						if (_.isArray(value)) {
							let values = []
							_.each(value, v => {
								values.push({
									value: v,
									label: v,
									selected: true
								})
							})
							selects.filtering(control, values)
						} else {
							selects.val(control, value)
						}
					}
				}
			}
			if (debug) {
				console.log(name + ': ' + value)
			}
		})
	},

	/**
	 * Saves the form fields contained in the given element to the model
	 *
	 * @param el the DOM element containing the form elements to be initialized
	 */
	readFromDom: function(el) {
		var data = {}
		// filter out invisible elements because if they're not shown to the
		// user they probably shouldn't be sent to the server
		$(el).find(query).filter(':visible:not([type=radio])').each(function(index, control) {
			// important to use attr to pick up names for our custom controls.
			// Also put this above retrieving the value so it's easier to debug
			var name = $(control).attr('name')

			var value
			if (control.type === 'checkbox') {
				value = control.checked
			} else if (!_.isUndefined($(control).data('rome-id'))) {
				value = getIsoDate(control)
			} else if ($(control).data('val')) {
				value = $(control).data('val')()
			// needed in case a select is wrapped in a marked-field
			} else if (control.tagName.toLowerCase() === 'select') {
				value = $(control).val()
			} else if ($(control).data('view')) {
				value = $(control).data('view').val()
			} else {
				value = $(control).val()
			}

			data[name] = value
			if (debug) {
				console.log(name + ': ' + value)
			}
		})
		$(el).find('input[type=radio]:checked:visible').each(function(index, control) {
			var name = $(control).attr('name')
			var value = $(control).attr('value')
			data[name] = value
			if (debug) {
				console.log(name + ': ' + value)
			}
		})
		// read values from on/off toggles
		$(el).find('.on-off-toggle:visible').each((index, el) => {
			let control = $(el).find('[name]')
			var name = $(control).attr('name')
			let value = control[0].checked
			data[name] = value
			if (debug) {
				console.log(name + ': ' + value)
			}
		})

		return data
	},

	/**
	 * Returns the marked fields found in the given element
	 * @param el the element to search for marked fields
	 * @param {Boolean} [visibleOnly=true] indicates whether only visible marked
	 * fields should be returned
	 * @returns {MarkedField[]} the marked fields
	 */
	getMarkedFields: function(el, visibleOnly) {
		let visibleSelector = _.isUndefined(visibleOnly) || visibleOnly ? ':visible' : ''
		return _.map($(el).find('.marked-field' + visibleSelector), function(el) {
			// need to grab the first one because the rich selects put a hidden
			// input in the DOM after the select
			return $(el).find('input, select, textarea, .rollup-selector').first().data('view')
		})
	},

	// test all the marked-fields for errors, and return the views with errors.
	// if there are no errors, returns null.  You can pass in an element to
	// search for marked-fields, or an array of marked-fields to check
	test: function(el) {
		var fields = el instanceof $ ? this.getMarkedFields(el) : el
		var errors = _.transform(fields, function(result, field) {
			if (field && field.test()) {
				result.push(field)
			}
		}, [])

		// Also, check to see if there are any file uploaders which are still empty
		el = $(el)
		_.each(el.find('.file-uploader:visible input'), input => {
			input = $(input)
			if (input.val() == '' && !input.hasClass('has-files')) {
				errors.push(i18n.get('common.error.mustSelect.file'))
			}
		})

		var answer = _.isEmpty(errors) ? null : errors
		if (debug) {
			console.log('test')
			console.dir(answer)
		}
		return answer
	},

	// validate the form, display any relevant error messages and return an
	// array of errors found.  if there are no errors, returns null.  You can
	// pass in an element to search for marked-fields, or an array of
	// marked-fields to check
	validate: function(el) {
		var fields = el instanceof $ ? this.getMarkedFields(el) : el
		var answer = _.compact(_.invokeMap(fields, 'validate'))
		answer = _.isEmpty(answer) ? null : answer
		if (debug) {
			console.log('validate')
			console.dir(answer)
		}
		return answer
	}

}

export default formHelper

formHelper.__filename__ = 'components/form-helper.js'
